import { useState } from 'react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const SEGMENTOS = [
  'Equipamentos / Máquinas',
  'Distribuição',
  'Indústria',
  'Energia / Climatização',
  'Tecnologia / TI',
  'Serviços Especializados',
  'Outro',
];

const VOLUMES = [
  'Até 30 mensagens/dia',
  '30–100 mensagens/dia',
  '100–300 mensagens/dia',
  '300+ mensagens/dia',
];

function maskPhone(value: string): string {
  const d = value.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

interface FieldProps {
  label: string;
  error?: boolean;
  children: React.ReactNode;
}

function Field({ label, error, children }: FieldProps) {
  return (
    <div>
      <label className="font-b2bbody text-sm font-semibold text-gray-700 block mb-1.5">{label}</label>
      {children}
      {error && (
        <p className="font-b2bbody text-xs text-red-500 mt-1">Campo obrigatório</p>
      )}
    </div>
  );
}

const inputClass =
  'w-full font-b2bbody text-sm text-gray-900 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 outline-none focus:border-coral focus:bg-white transition-colors placeholder:text-gray-400';

interface FinalCTAProps {
  formRef?: React.RefObject<HTMLDivElement>;
}

export default function FinalCTA({ formRef }: FinalCTAProps) {
  const { ref, visible } = useScrollAnimation(0.07);

  const [form, setForm] = useState({
    nome: '',
    whatsapp: '',
    empresa: '',
    segmento: '',
    volume: '',
  });
  const [errors, setErrors] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);

  const update = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: false }));
  };

  const validate = () => {
    const newErrors: Record<string, boolean> = {};
    if (!form.nome.trim()) newErrors.nome = true;
    if (!form.whatsapp.trim() || form.whatsapp.replace(/\D/g, '').length < 10) newErrors.whatsapp = true;
    if (!form.empresa.trim()) newErrors.empresa = true;
    if (!form.segmento) newErrors.segmento = true;
    if (!form.volume) newErrors.volume = true;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);

    // Build WhatsApp pre-filled message — strip "mensagens/dia" from volume option
    const volumeClean = form.volume.replace(/\s*mensagens\/dia/gi, '').trim();
    const mensagem = `Olá, sou ${form.nome}, trabalho na empresa ${form.empresa}, do setor de ${form.segmento}. Recebemos cerca de ${volumeClean} mensagens por dia no WhatsApp e quero ver a demo do agente de IA da Automat.`;
    const waUrl = `https://wa.me/5519988332466?text=${encodeURIComponent(mensagem)}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
    setLoading(false);

    // Reset form after redirect
    setForm({ nome: '', whatsapp: '', empresa: '', segmento: '', volume: '' });
    setErrors({});
  };

  return (
    <section
      id="cta"
      className="relative py-24 lg:py-32 px-6 overflow-hidden"
      style={{ background: 'linear-gradient(135deg, #0A1628 0%, #112448 55%, #0A1628 100%)' }}
    >
      {/* Decorative glow */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] pointer-events-none"
        style={{ background: 'radial-gradient(ellipse, rgba(233,69,96,0.12) 0%, transparent 70%)' }}
      />
      <div
        className="absolute bottom-0 left-0 right-0 h-px"
        style={{ background: 'linear-gradient(90deg, transparent, rgba(233,69,96,0.3), transparent)' }}
      />

      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Header */}
        <div
          className={`text-center mb-14 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
          ref={ref}
        >
          <div className="flex justify-center mb-5">
            <span className="font-b2bbody text-xs font-semibold tracking-widest uppercase border border-coral/30 text-coral rounded-full px-4 py-1.5">
              Demo Gratuita
            </span>
          </div>
          <h2 className="font-b2b text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-white leading-tight mb-5 max-w-3xl mx-auto">
            Veja o agente de IA atendendo sobre os{' '}
            <span className="text-coral">SEUS</span> produtos
          </h2>
          <p className="font-b2bbody text-white/60 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Preencha abaixo e receba uma demo personalizada para o seu negócio.
            Em menos de 5 minutos você vai entender por que seus concorrentes já estão usando IA.
          </p>
        </div>

        {/* Form card */}
        <div
          className={`max-w-[560px] mx-auto transition-all duration-700 delay-150 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          ref={formRef}
        >
          <div
            className="bg-white rounded-3xl p-8 sm:p-10"
            style={{ boxShadow: '0 24px 80px rgba(0,0,0,0.35)' }}
          >
            <div className="flex items-center gap-2 mb-7">
              <div className="w-8 h-8 rounded-lg bg-coral/10 flex items-center justify-center">
                <i className="ri-user-add-line text-coral text-sm" />
              </div>
              <h3 className="font-b2b text-base font-bold text-navy">Solicite sua demo gratuita</h3>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <Field label="Nome completo" error={errors.nome}>
                <input
                  type="text"
                  name="nome"
                  placeholder="João Silva"
                  value={form.nome}
                  onChange={(e) => update('nome', e.target.value)}
                  className={`${inputClass} ${errors.nome ? 'border-red-300 bg-red-50' : ''}`}
                />
              </Field>

              <Field label="WhatsApp (com DDD)" error={errors.whatsapp}>
                <input
                  type="tel"
                  name="whatsapp"
                  placeholder="(11) 99999-9999"
                  value={form.whatsapp}
                  onChange={(e) => update('whatsapp', maskPhone(e.target.value))}
                  className={`${inputClass} ${errors.whatsapp ? 'border-red-300 bg-red-50' : ''}`}
                />
              </Field>

              <Field label="Nome da empresa" error={errors.empresa}>
                <input
                  type="text"
                  name="empresa"
                  placeholder="Empresa Ltda."
                  value={form.empresa}
                  onChange={(e) => update('empresa', e.target.value)}
                  className={`${inputClass} ${errors.empresa ? 'border-red-300 bg-red-50' : ''}`}
                />
              </Field>

              <Field label="Segmento" error={errors.segmento}>
                <select
                  name="segmento"
                  value={form.segmento}
                  onChange={(e) => update('segmento', e.target.value)}
                  className={`${inputClass} cursor-pointer ${errors.segmento ? 'border-red-300 bg-red-50' : ''}`}
                >
                  <option value="">Selecione seu segmento</option>
                  {SEGMENTOS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Field>

              <Field label="Volume de mensagens por dia" error={errors.volume}>
                <select
                  name="volume"
                  value={form.volume}
                  onChange={(e) => update('volume', e.target.value)}
                  className={`${inputClass} cursor-pointer ${errors.volume ? 'border-red-300 bg-red-50' : ''}`}
                >
                  <option value="">Selecione o volume</option>
                  {VOLUMES.map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </select>
              </Field>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="whitespace-nowrap w-full font-b2bbody font-bold text-base py-4 px-6 rounded-xl text-white cursor-pointer transition-all duration-200 hover:scale-[1.02] hover:brightness-110 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2.5"
                  style={{
                    background: 'linear-gradient(135deg, #00B96B, #009955)',
                    boxShadow: loading ? 'none' : '0 4px 20px rgba(0,185,107,0.35)',
                  }}
                >
                  {loading ? (
                    <>
                      <svg className="animate-spin w-5 h-5 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                      </svg>
                      Enviando...
                    </>
                  ) : (
                    <>
                      <i className="ri-whatsapp-line text-xl" />
                      Solicitar Demo Gratuita
                    </>
                  )}
                </button>

                <div className="flex items-center justify-center gap-4 mt-4">
                  {['Sem compromisso', 'Sem cartão de crédito', 'Resposta em minutos'].map((item) => (
                    <div key={item} className="flex items-center gap-1">
                      <i className="ri-check-line text-green-500 text-xs" />
                      <span className="font-b2bbody text-xs text-gray-400">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </form>
          </div>

          {/* Trust indicators below card */}
          <div className="flex items-center justify-center gap-6 mt-6">
            {[
              { icon: 'ri-lock-line', label: 'Dados protegidos' },
              { icon: 'ri-shield-check-line', label: 'Sem spam' },
              { icon: 'ri-time-line', label: 'Resposta rápida' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-1.5">
                <i className={`${item.icon} text-white/30 text-sm`} />
                <span className="font-b2bbody text-white/30 text-xs">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
